



































































import { Component, Vue, Watch } from 'vue-property-decorator';
import { Route, Location } from 'vue-router';
import { mapGetters, mapState } from 'vuex';
import * as Sentry from '@sentry/vue';

import { meta } from 'frontend/utils/decorators';
import { lockScroll } from 'frontend/utils/scroll';
import { inExperimentGetter } from 'frontend/utils/experiment';
import {
  lessonSetupPreInit,
  registerLessonAsHost,
  registerLessonScrollEvent,
} from 'frontend/views/lessons/lesson/lessonSetupExtraJs';

import AdminMenu from 'frontend/components/AdminMenu.vue';
import Banner from 'frontend/components/Banner.vue';
import ReportProblem from 'frontend/views/lessons/lesson/ReportProblem.vue';
import LessonLoadingScreen from 'frontend/views/lessons/lesson/LessonLoadingScreen.vue';
import StreakButtonWrapper from 'frontend/components/StreakButtonWrapper.vue';
import DesktopOnlyModal from 'frontend/components/DesktopOnlyModal.vue';

import {
  User,
  AdminMenuItem,
  AdminMenuData,
  MobileGetter,
  Experiment,
  unreachableCase,
  StreakStats,
  StreakNotificationType,
  FeatureSwitches,
  Nullable,
  ClientType,
  Optional,
  ExperimentGetter,
} from 'frontend/types';
import { EndOfLessonAction, Lesson, UserStateData } from 'frontend/types/lessons/lesson';
import { lessonBaseUrlParams } from 'frontend/views/lessons/editor/utils';
import {
  LessonResetStateInputs,
  LessonSaveStateInputs,
  MarkSimsonTutorialAsSeenInputs,
} from 'frontend/types/lessons/requests';
import { pushLocationWithoutFetch } from 'frontend/router';
import { track } from 'frontend/utils/analytics';

import globalStyles from '~styles/quiz-v3.3/index.lazy.scss';
import { throttle } from 'frontend/utils/timing';
import { initHotjar } from 'frontend/utils/hotjar';
import { loadSounds, playActionSound, SoundAction } from 'frontend/utils/sounds';
import { StreakButtonElementData } from 'frontend/types/lessons/streaks';
import { nativeBridgeIsPresent, sendMessageAcrossNativeBridge } from 'frontend/utils/nativeBridge';
import retool from 'frontend/utils/retool';

import { XPPerDifficultyConfig } from 'frontend/store/modules/xp-per-solvable-config';
import LessonStreakUpdater from './LessonStreamUpdater';
import { InteractivesManifest } from 'visualizations/shared/interactivesManager/types';

// @ts-ignore
import lessonBundleHash from '../../../../lesson_bundle_hash.txt';
import LessonErrorScreen from 'frontend/views/lessons/lesson/LessonErrorScreen.vue';
import InteractivesManager from 'visualizations/shared/interactivesManager/InteractivesManager';
import { setupPortsForInteractive } from 'visualizations/shared/interactivesManager/defaultInteractivePortSetup';
import { ContentHashes } from 'frontend/types/lessons/editor';
import { elmConfigInteractiveToolkit } from '@brilliant/standalone-interactives';
import { javascriptInteractiveToolkit } from 'visualizations/shared/interactivesManager/toolkits/JavascriptInteractive';
import { LESSON_SCROLL_EFFECT_DELAY_MS } from 'visualizations/shared/envelopeConstants';
import { REPLACE_ERROR } from 'frontend/utils/asyncThrottle';

export interface TrackingProperties {
  lesson_id: Optional<number>;
  lesson_version: Optional<string>;
  lesson_type: Optional<number>;
  lesson_type_name: Optional<string>;
  draft: Optional<boolean>;
  course_id: Optional<number>;
  course_version_id: Optional<number>;
  chapter_id: Optional<number>;
  window_width: Optional<number>;
  window_height: Optional<number>;
  screen_width: Optional<number>;
  screen_height: Optional<number>;
  device_pixel_ratio: Optional<number>;
}

interface SaveStateData {
  lesson_state: object;
  blocks_completed: number;
  blocks_total: number;
  progress_data: object;
}

interface ElmMessageSaveState {
  name: 'saveState';
  data: SaveStateData;
}

interface ElmMessageResetState {
  name: 'resetState';
}

interface ElmMessageNarrativeBlockChange {
  name: 'blockIndexChanged';
  blockIndex: number;
}

interface ElmMessageFailedToLoad {
  name: 'failedToLoad';
  error: string;
}

interface ElmMessageEndOfLessonAction {
  name: 'endOfLessonAction';
  data: SaveStateData;
  url: string;
}

interface ElmMessageShowEndstate {
  name: 'showLessonEndstate';
  data: SaveStateData;
}

interface ElmMessageShowReportProblem {
  name: 'showReportProblem';
}

interface ElmMessageReportWarning {
  name: 'reportWarning';
  warning: string;
  extra_info: Record<string, any>;
}

interface ElmMessageScrollLock {
  name: 'scrollLock';
  locked: boolean;
}

interface ElmMessageScroll {
  name: 'scroll';
  options: {
    yOffset: number;
    smooth: boolean;
    relativeTo: 'elementTop' | 'windowBottom' | 'windowTop';
    elementId?: string;
  };
}

interface ElmMessageCopyEnvInfo {
  name: 'copyEnvInfo';
}

interface ElmMessageLegacyMobileNativeCloseButtonClicked {
  name: 'mobileNativeCloseButtonClicked';
}

interface ElmMessageCloseButtonClicked {
  name: 'closeButtonClicked';
}

interface ElmMessageTrackEvent {
  name: 'trackEvent';
  eventStream: string;
  properties: Record<string, unknown>;
}

interface ElmMessagePlaySoundAction {
  name: 'playActionSound';
  action: SoundAction;
}

interface ElmMessagePushState {
  name: 'browserHistoryPushState';
}

interface ElmMessageMarkSimsonTutorialAsSeen {
  name: 'markSimsonTutorialAsSeen';
}

type ElmMessage =
  | ElmMessageSaveState
  | ElmMessageResetState
  | ElmMessageNarrativeBlockChange
  | ElmMessageFailedToLoad
  | ElmMessageEndOfLessonAction
  | ElmMessageShowEndstate
  | ElmMessageShowReportProblem
  | ElmMessageLegacyMobileNativeCloseButtonClicked
  | ElmMessageReportWarning
  | ElmMessageCloseButtonClicked
  | ElmMessageTrackEvent
  | ElmMessagePlaySoundAction
  | ElmMessageScrollLock
  | ElmMessageScroll
  | ElmMessagePushState
  | ElmMessageMarkSimsonTutorialAsSeen
  | ElmMessageCopyEnvInfo;

type UserEnvironment = 'Desktop' | 'MobileWeb' | 'MobileNative' | 'Tablet';

const BANNER_VISIBLE_CLASS_NAME = 'banner-visible';

@Component({
  components: {
    LessonErrorScreen,
    AdminMenu,
    Banner,
    ReportProblem,
    StreakButtonWrapper,
    DesktopOnlyModal,
    LessonLoadingScreen,
    LessonEndstate: () =>
      // tslint:disable-next-line:space-in-parens
      import(
        /* webpackChunkName: 'LessonEndstate' */ 'frontend/views/lessons/lesson/LessonEndstate.vue'
      ),
  },
  computed: {
    ...mapState('globals', ['user', 'experiments']),
    ...mapState('lesson', ['lesson']),
    ...mapState('userState', ['userState']),
    ...mapState('streakPopup', ['streakPopupVisible', 'streakNotificationType']),
    ...mapGetters('globals', ['streakData', 'featureSwitches', 'inExperiment']),
    ...mapGetters('pageHeader', ['isMobile', 'isNative', 'clientType']),
    ...mapGetters('banner', ['bannerHeight', 'currentBanner']),
    ...mapGetters('xpPerSolvableConfig', ['canEarnPerSolvableXP', 'xpPerDifficulty']),
    ...mapGetters('response', ['baseUrl']),
  },
  beforeRouteEnter(to: Route, from: Route, next): void {
    next(vm => {
      (vm as LessonView).externalReferrer = from.name === null;
    });
  },
})
export default class LessonView extends Vue {
  private readonly user!: User;
  private readonly userState!: Optional<UserStateData>;
  private readonly clientType!: ClientType;
  private readonly lesson!: Lesson;
  private readonly streakData!: Optional<StreakStats>;
  private readonly experiments!: { [key: string]: Experiment };
  private readonly isMobile!: MobileGetter;
  private readonly isNative!: boolean;
  private readonly featureSwitches!: FeatureSwitches;
  private inExperiment!: ExperimentGetter;
  private readonly canEarnPerSolvableXP!: boolean;
  private readonly baseUrl!: string;
  private readonly xpPerDifficulty!: Nullable<XPPerDifficultyConfig>;
  private elmScript!: HTMLScriptElement;
  private elmApp!: any;
  private isRouting = false;
  private loadingError: 'missingJs' | 'badFlags' | 'compilerError' | null = null;
  private compilerErrorMessage: string | null = null;
  private streakUpdater: LessonStreakUpdater = new LessonStreakUpdater();
  private streakPopupVisible!: boolean;
  private streakNotificationType!: StreakNotificationType;
  private scrolledPixels = 0;
  private bannerHeight!: number;
  private currentBanner!: typeof Banner;
  private compiledJs: string | null = null;
  private instantLoad: boolean = true;
  private reloadTimeout: NodeJS.Timeout | null = null;
  private lessonJsLoaded: boolean = false;
  private contentHashes?: ContentHashes;
  private universeState?: string;
  private universeCommit?: string;
  private externalReferrer: boolean = false;

  private get visibleBannerHeight(): number {
    return this.scrolledPixels >= this.bannerHeight ? 0 : this.bannerHeight - this.scrolledPixels;
  }

  private get isPreview(): boolean {
    return this.userState?.user_state_id === -1;
  }

  private get isBannerVisible(): boolean {
    return this.currentBanner && this.visibleBannerHeight > 0;
  }

  private get editorVersionsUrl(): string {
    if (this.featureSwitches.use_retool_campgrounds) {
      return retool.lessonVersions(lessonBaseUrlParams(this.$route).lessonSlug);
    }
    return this.$router.resolve({
      name: 'lesson_versions',
      params: lessonBaseUrlParams(this.$route),
    }).href;
  }

  private get editUrl(): string {
    return this.$router.resolve({
      name: 'lesson_edit',
      params: {
        ...lessonBaseUrlParams(this.$route),
        versionName: this.lesson.version_name,
      },
    }).href;
  }

  private get getPreviewToken(): Nullable<string> {
    const previewTokenParams = this.$route.query.preview_token;

    if (Array.isArray(previewTokenParams)) {
      return previewTokenParams[0] || null;
    }

    return previewTokenParams || null;
  }

  private get icpUrl(): string {
    return this.$router.resolve({
      name: 'explorations_chapter',
      params: lessonBaseUrlParams(this.$route),
    }).href;
  }

  private adminMenuData(): AdminMenuData {
    const badges: string[] = [];
    const items: AdminMenuItem[] = [];

    items.push({
      link: [this.editUrl, `Edit this version`],
    });
    items.push(`${this.lesson.state} ${this.lesson.version_name}`);

    const stateString = this.universeState ? `${this.universeState.substring(10)}` : 'No Repo';
    const commitString = this.universeCommit
      ? `${this.universeCommit.substring(0, 7)}`
      : 'No Commit';
    items.push(`${stateString} - ${commitString}`);

    items.push({
      link: [this.editorVersionsUrl, 'Show all versions'],
    });

    badges.push(this.lesson.state);
    badges.push(this.lesson.version_name);

    return {
      badges,
      items,
    };
  }

  private get lessonSlug() {
    return this.$route.params.lessonSlug;
  }

  private get courseSlug() {
    return this.$route.params.courseSlug;
  }

  private get showAdminFeatures(): boolean {
    return this.user.is_staff || this.lesson.is_editable === true;
  }

  private get blockIndexInRoute(): number | null {
    return this.$route.params.blockIndex ? parseInt(this.$route.params.blockIndex, 10) : null;
  }

  private get streakElementData(): StreakButtonElementData {
    return {
      streakPopupVisible: this.streakPopupVisible,
      streakPopupNotificationType: this.streakNotificationType,
      streaksData: this.streakData,
    };
  }

  private get showReportProblem(): boolean {
    return !!this.user?.user_id;
  }

  private get userEnvironment(): UserEnvironment {
    if (process.env.VUE_ENV !== 'server') {
      if ('ontouchstart' in document.documentElement && document.body.clientWidth >= 768) {
        return 'Tablet';
      }
    }

    const isMobile = this.isMobile(this.$mq);
    if (isMobile && this.isNative) {
      return 'MobileNative';
    }
    if (isMobile) {
      return 'MobileWeb';
    }
    return 'Desktop';
  }

  private get showLoadingIndicator(): boolean {
    return (
      !this.instantLoad &&
      !this.lessonJsLoaded &&
      !this.loadingError &&
      !this.showDesktopOnlyModal &&
      !this.isNative // the native apps show their own loading animations
    );
  }

  private get showErrorScreen(): boolean {
    return !!this.loadingError && !this.showAdminFeatures;
  }

  private get showLesson(): boolean {
    return this.lessonJsLoaded && !this.showLoadingIndicator && !this.showErrorScreen;
  }

  private get showDesktopOnlyModal(): boolean {
    const isDesktopOnly = this.lesson?.desktop_only;
    const envIsNotDesktop = this.userEnvironment !== 'Desktop';

    return envIsNotDesktop && isDesktopOnly;
  }

  private get isLoggedOut(): boolean {
    return this.$route.name === 'logged_out_lesson';
  }

  private get endOfLessonAction(): Nullable<EndOfLessonAction> {
    if (!this.isLoggedOut) {
      return this.lesson.end_of_lesson_action_logged_in;
    }
    if (this.lesson.end_of_lesson_action_logged_out) {
      return this.lesson.end_of_lesson_action_logged_out;
    }
    // Handle default action for logged-out lessons
    const url = this.$router.resolve({
      name: 'logged_out_lesson_complete',
      params: {
        lessonSlug: this.lessonSlug,
      },
    }).href;
    const defaultAction: EndOfLessonAction = {
      button_url: url,
    };
    return defaultAction;
  }

  @meta
  private metaInfo() {
    return {
      title: this.lesson.title || 'Practice Math and Science',
    };
  }

  private get scrollHandler() {
    return throttle(() => {
      this.scrolledPixels = document.documentElement.scrollTop;
    }, 16);
  }

  private handleMessageBlockChange(newBlockIndex: number) {
    if (newBlockIndex !== this.blockIndexInRoute) {
      const newLocation: Location = {
        name: this.$route.name,
        params: {
          blockIndex: newBlockIndex.toString(),
        },
        query: this.$route.query,
      };
      this.isRouting = true;
      pushLocationWithoutFetch(this.$router, newLocation, () => {
        Vue.nextTick(() => (this.isRouting = false));
      });

      sendMessageAcrossNativeBridge('blockIndexChanged', { blockIndex: newBlockIndex });
    }
  }

  private lessonIsDraft() {
    return this.lesson.state === 'DRAFT';
  }

  @Watch('streakElementData')
  private streakElementDataChanged(val: StreakButtonElementData) {
    if (
      this.streakElementData.streakPopupVisible &&
      this.streakElementData.streakPopupNotificationType !== 'none'
    ) {
      // we are triggering the celebration... let's reset it
      // in the vuex store since the Lesson's streak custom-element
      // can't communicate with Vuex and if we don't, it will stay
      // "true" forever
      this.$store.dispatch('streakPopup/hidePopup');
    }
    this.streakUpdater.update(val);
  }

  @Watch('$route')
  private routeChanged(to: Route) {
    // This is called if the user presses the back/forward buttons; that will
    // have us load a different block.
    if (!this.isRouting) {
      this.elmApp.ports.fromJs.send({
        name: 'updateBlockIndex',
        blockIndex: parseInt(to.params.blockIndex, 10),
      });
    }
  }

  private shouldAutoShowSimsonTutorial() {
    const TUTORIAL_EXPERIMENT = 'simson_tutorial_experiment';
    const EXPERIMENT_TREATMENT = 'experiment';
    const inExperiment = inExperimentGetter(this.experiments)(
      TUTORIAL_EXPERIMENT,
      EXPERIMENT_TREATMENT,
    );

    const lastSeenTime = this.user.last_simson_tutorial_view_time;
    if (lastSeenTime) {
      const inputDate = new Date(lastSeenTime);
      const currentDate = new Date();
      const msPerDay = 1000 * 60 * 60 * 24;
      const daysDifference = (currentDate.getTime() - inputDate.getTime()) / msPerDay;

      return inExperiment && daysDifference > 60;
    }

    return inExperiment;
  }

  private async initElm() {
    // convert some-example-lesson to SomeExampleLesson
    const lessonKey = this.lessonSlug
      .split('-')
      .map(c => `${c[0].toUpperCase()}${c.substring(1)}`)
      .join('');

    // If the lesson is a draft, we don't want to throw away user state if there
    // have been changes as this can be frustrating for authors.
    // Instead, we just use the lesson's hashes to convince the lesson that it
    // shouldn't remove any saved state.
    const userState = { ...this.userState };
    if (this.lessonIsDraft() && this.contentHashes) {
      userState.content_hashes = this.contentHashes;
    }

    // Warning: Adding required flags here will break older versions of the native apps
    // any new flag we want to introduce should be optional.
    this.elmApp = (window as any).Elm[`Quiz${lessonKey}Main`].init({
      node: this.$refs.lesson,
      flags: {
        userState,
        quizTitle: this.lesson.title || '',
        editorUrl: this.editUrl || '',
        courseName: this.lesson.course_name,
        lessonColor: this.lesson.bookend_color || null,
        lessonCardImgSrc: this.lesson.bookend_image_url || null,
        lessonVersionId: this.lesson.version_id,
        blockDeeplink: this.blockIndexInRoute,
        userIsStaff: this.user.is_staff,
        userIsExternalAuthor: this.user.is_external_lesson_author,
        experiments: this.experiments,
        lessonVersionContentHashes: this.contentHashes || this.lesson.content_hashes || null,
        backButtonUrl: this.icpUrl,
        hideAdminFeatures: this.user.hide_admin_features,
        previewToken: this.getPreviewToken,
        canEarnPerSolvableXP: this.canEarnPerSolvableXP,
        xpPerDifficulty: this.xpPerDifficulty,
        shouldAutoShowSimsonTutorial: this.shouldAutoShowSimsonTutorial(),
        showReportProblemButton: this.showReportProblem,
        showLessonBookend: !this.isLoggedOut,
        endOfLessonAction: this.endOfLessonAction,
      },
    });

    this.elmApp.root = this.$refs.container;
    this.elmApp.ports?.toJs?.subscribe(this.handleElmMessage);

    // This listens for analytics events coming from interactives!
    // We haven't moved them over to the new Effects system, so this seems the simplest way
    // to integrate them into v3 for now.
    this.elmApp.ports?.analytics?.subscribe((data: object) => {
      if (nativeBridgeIsPresent()) {
        sendMessageAcrossNativeBridge('analyticsEvent', {
          stream: 'interactives_actions',
          paramsAsString: JSON.stringify({ ...this.getTrackingProperties(), ...data }),
        });
        return;
      }

      track('interactives_actions', { ...this.getTrackingProperties(), ...data });
    });

    if (process.env.VUE_ENV !== 'server' && this.elmApp.ports) {
      // TODO LIX-1870
      // Many of the interactives are still running inside of the main lesson Elm app, so for now,
      // we need to set up ports for those as well by calling setupPortsForInteractive.
      // Once Phase 2 of the Refactor is done, we can remove this line,
      // since the Lesson Elm app won't have any more interactives in it.
      await setupPortsForInteractive({ interactiveApp: this.elmApp, container: this.elmApp.root });
      registerLessonAsHost(this.elmApp);
      registerLessonScrollEvent(this.elmApp);

      window.addEventListener('popstate', event => {
        this.elmApp.ports.fromJs.send({ name: 'popstateReceived' });
      });

      window.visualViewport?.addEventListener('resize', event => {
        this.elmApp.ports.fromJs.send({ event, name: 'visualViewportResize' });
      });
    }
  }

  private async saveState(data: SaveStateData, onError: (e: any) => void): Promise<void> {
    const inputs: LessonSaveStateInputs = {
      params: lessonBaseUrlParams(this.$route),
      data: {
        version_id: this.lesson.version_id,
        is_preview: this.isPreview,
        content_hashes: this.contentHashes,
        from_llp: this.$route.query.from_llp as string,
        ...data,
      },
    };
    try {
      await this.$store.dispatch('userState/save', inputs);
    } catch (e) {
      onError(e);
    }
  }

  private async handleElmMessage(msg: ElmMessage) {
    if (msg.name === 'saveState') {
      if (this.userState) {
        await this.saveState(msg.data, e => {
          if (e !== REPLACE_ERROR) {
            this.reportLessonError('Failed to save user state.', { error: e });
          }
        });
      }
    } else if (msg.name === 'resetState') {
      if (this.userState) {
        const inputs: LessonResetStateInputs = {
          params: lessonBaseUrlParams(this.$route),
          data: { version_id: this.lesson.version_id },
        };
        this.$store.dispatch('userState/reset', inputs).then(() => location.reload());
      }
    } else if (msg.name === 'blockIndexChanged') {
      return this.handleMessageBlockChange(msg.blockIndex);
    } else if (msg.name === 'showReportProblem') {
      await this.$store.dispatch('lesson/showReportProblem');
    } else if (msg.name === 'browserHistoryPushState') {
      window.history.pushState({}, '');
    } else if (msg.name === 'markSimsonTutorialAsSeen') {
      const inputs: MarkSimsonTutorialAsSeenInputs = {
        params: lessonBaseUrlParams(this.$route),
      };
      this.$store.dispatch('profile/markSimsonTutorialAsSeen', inputs);
    } else if (msg.name === 'failedToLoad') {
      this.loadingError = 'badFlags';
    } else if (msg.name === 'endOfLessonAction') {
      if (this.userState) {
        await this.saveState(msg.data, e =>
          this.reportLessonError('Failed to save user state before end-of-lesson action.', {
            error: e,
            note: 'This likely means that they did not get credit for their XP',
          }),
        );
      }
      await this.$router.push({ path: msg.url });
      return;
    } else if (msg.name === 'showLessonEndstate') {
      if (!this.userState) {
        pushLocationWithoutFetch(this.$router, { name: 'homepage' }, () => {
          // noop
        });
        return;
      }
      if (msg.data) {
        // The backend needs the user's final state to calculate data for the endstate, so we
        // need to save one last time and wait for the request to finish before showing the
        // endstate.
        await this.saveState(msg.data, e =>
          this.reportLessonError('Failed to save user state before showing lesson endstate.', {
            error: e,
            note: 'This likely means that they did not get credit for their XP',
          }),
        );
      }
      pushLocationWithoutFetch(
        this.$router,
        {
          name: 'lesson_endstate',
          params: this.$route.params,
          query: { from_llp: this.$route.query.from_llp },
        },
        () => {
          // noop
        },
      );
    } else if (msg.name === 'reportWarning') {
      this.reportLessonError(msg.warning, msg.extra_info);
    } else if (msg.name === 'scrollLock') {
      lockScroll(msg.locked);
    } else if (msg.name === 'scroll') {
      setTimeout(() => {
        const { yOffset, relativeTo, smooth } = msg.options;
        const behavior = smooth ? 'smooth' : 'instant';

        let top = 0;

        if (relativeTo === 'windowTop') {
          top = 0;
        } else if (relativeTo === 'windowBottom') {
          top = document.documentElement.scrollHeight;
        } else if (relativeTo === 'elementTop') {
          const foundElement = document.getElementById(msg.options.elementId || '');
          if (foundElement) {
            top = foundElement.getBoundingClientRect().top + window.scrollY;
          } else {
            top = 0;
          }
        }

        window.scroll({
          top: top + yOffset,
          behavior: behavior as ScrollBehavior,
        });
      }, LESSON_SCROLL_EFFECT_DELAY_MS);
    } else if (msg.name === 'copyEnvInfo') {
      this.addLessonInfoToClipboard();
    } else if (msg.name === 'closeButtonClicked') {
      this.closeLesson();
    } else if (msg.name === 'mobileNativeCloseButtonClicked') {
      // Do nothing - this message exists to support old clients using
      // problemsvue
      //
      // This message can be safely removed once we're confident that there are
      // a small enough number of old mobile clients that have not been updated.
    } else if (msg.name === 'playActionSound') {
      if (this.user.enable_sound_effects || this.isLoggedOut) {
        if (msg.action === 'endstate' && this.isLoggedOut) {
          // No-op: do not play sound at end of logged-out lesson because it gets cut off.
        } else {
          await playActionSound(msg.action);
        }
      }
    } else if (msg.name === 'trackEvent') {
      if (nativeBridgeIsPresent()) {
        const eventData = {
          ...this.getTrackingProperties(),
          ...msg.properties,
        };
        sendMessageAcrossNativeBridge('analyticsEvent', {
          stream: msg.eventStream,
          paramsAsString: JSON.stringify(eventData),
        });
        return;
      }

      // Conditionally exclude from Amplitude
      const isAmplitudeExcludedEvent =
        msg.eventStream === 'student_action' && msg.properties.action === 'clicked_back_to_problem';
      const trackingOpts: Parameters<typeof track>[2] = isAmplitudeExcludedEvent
        ? { exclude: ['Amplitude'] }
        : undefined;

      track(msg.eventStream, { ...this.getTrackingProperties(), ...msg.properties }, trackingOpts);
    } else {
      unreachableCase(msg);
    }
  }

  private async fetchLesson() {
    const minifiedResponse = await fetch(this.lesson.optimized_cdn_url);
    if (minifiedResponse.ok) {
      const receivedJSON = await minifiedResponse.json();
      await this.loadLessonJSON(receivedJSON);
    } else {
      const response = await fetch(this.lesson.cdn_url);
      if (!response.ok) {
        this.reloadTimeout = setTimeout(this.fetchLesson, 500);
        return;
      }

      const receivedJSON = await response.json();
      await this.loadLessonJSON(receivedJSON);
    }
  }

  private async loadLessonJSON(json: {
    interactivesManifest: InteractivesManifest;
    compiledJs: string;
    contentHashes?: ContentHashes;
    errorMessage?: string;
    universeCommit?: string;
    universeState?: string;
  }) {
    if (json.universeCommit) {
      this.universeCommit = json.universeCommit;
    }

    if (json.universeState) {
      this.universeState = json.universeState;
    }

    if (json.errorMessage) {
      this.loadingError = 'compilerError';
      this.compilerErrorMessage = json.errorMessage;
      return;
    }
    InteractivesManager.global.setManifest(json.interactivesManifest);
    this.compiledJs = json.compiledJs;
    if (json.contentHashes) {
      this.contentHashes = json.contentHashes;
    }
    await this.loadScript();

    // Here we wait for Elm to paint its first frame and _then_ set
    // lessonJsLoaded to true - updating the rest of the UI.
    //
    // This avoids a weird UI state for one frame which gives the user a really
    // jarring experience.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.lessonJsLoaded = true;

        if (nativeBridgeIsPresent()) {
          sendMessageAcrossNativeBridge('loaded', {
            version_id: this.lesson.version_id,
            version_name: this.lesson.version_name,
          });
        }
      });
    });
  }

  private async reportLessonError(message: string, extra: Record<string, unknown> = {}) {
    Sentry.withScope(scope => {
      scope.setTag('feature', 'v3-runtime');
      scope.setExtras(extra);
      Sentry.captureMessage(message, extra);
    });
  }

  private async loadScript() {
    function makeJsProfilable(input: string) {
      const rule1 = /var ([^=]+)( = F\d\([^f]+function)[^(]\(/gim;
      const replacement1 = 'var $1$2 __$1( ';
      const rule2 = /(\sA\d\([\s]+)([^,]+)(,[\s]+)(function[^(]+)\(/gm;
      const replacement2 = '$1$2$3$4___$2(';
      return input.replaceAll(rule1, replacement1).replaceAll(rule2, replacement2);
    }

    this.elmScript = document.createElement('script');
    if (process.env.VUE_ENV !== 'server') {
      await lessonSetupPreInit({
        streakButtonUpdater: this.streakUpdater,
      });
      this.$nextTick(() => {
        // Provide initial data to the streaks button on vue's nextTick.
        // if we do it right now, the custom element isn't ready/connected.
        this.streakUpdater.update(this.streakElementData);
      });
    }
    const jsToLoad =
      this.$route.query.profilable === 'true' && this.compiledJs
        ? makeJsProfilable(this.compiledJs)
        : this.compiledJs;
    this.elmScript.innerHTML = jsToLoad || 'console.error("Missing JS");';
    document.body.appendChild(this.elmScript);
    await this.initElm();
  }

  private getTrackingProperties(): TrackingProperties {
    return {
      lesson_id: this.lesson.lesson_id,
      lesson_version: this.lesson.version_name,
      lesson_type: 3,
      lesson_type_name: 'course_lesson',
      draft: this.lessonIsDraft(),
      course_id: this.lesson.course_id,
      course_version_id: this.lesson.course_version_id,
      chapter_id: this.lesson.chapter_id,
      window_width: window.innerWidth,
      window_height: window.innerHeight,
      screen_width: window.screen.width,
      screen_height: window.screen.height,
      device_pixel_ratio: window.devicePixelRatio,
    };
  }

  @Watch('isBannerVisible')
  private initBanner(): void {
    if (this.isBannerVisible) {
      document.body.classList.add(BANNER_VISIBLE_CLASS_NAME);
    } else {
      document.body.classList.remove(BANNER_VISIBLE_CLASS_NAME);
    }
  }

  private addLessonInfoToClipboard(): void {
    if (this.clientType == null || this.lesson == null || this.user == null) {
      return;
    }

    const lessonUrl = new URL(document.location.href);
    lessonUrl.searchParams.set('version_name', this.lesson.version_name);

    let client;
    if (this.clientType.is_android) {
      client = `android app ${this.clientType.app_version}`;
    } else if (this.clientType.is_ios) {
      client = `ios app ${this.clientType.app_version}`;
    } else if (this.clientType.is_mobile) {
      client = `mobile web`;
    } else {
      client = 'desktop web';
    }

    const toCopy = `~~~
Lesson URL: ${lessonUrl.href}
Lesson Version: ${this.lesson.state} ${this.lesson.version_name}
User Profile: ${document.location.origin + this.user.profile_url}
Client: ${client}
~~~`;
    navigator.clipboard.writeText(toCopy);
  }

  private destroyBanner(): void {
    document.body.classList.remove(BANNER_VISIBLE_CLASS_NAME);
    window.removeEventListener('scroll', this.scrollHandler);
  }

  private async mounted() {
    setTimeout(() => {
      this.instantLoad = false;
    }, 600);
    window.addEventListener('scroll', this.scrollHandler);
    if (!this.lesson.lesson_id) {
      // If we're here and it's an author, we're almost certainly waiting for the redirect to
      // /versions/ to kick in. If we're here and it's not an author, 1) that should be impossible,
      // and 2) things aren't going to render anyway.
      // The point of this is to avoid flashing the "oh no!" message when redirecting to /versions/
      return;
    }

    InteractivesManager.global.setConfig({
      toolkits: [elmConfigInteractiveToolkit, javascriptInteractiveToolkit],
      portSetupFunction: setupPortsForInteractive,
      experiments: this.experiments,
    });

    globalStyles.use();

    if (this.lesson.using_cdn && this.lesson.server_bundle_hash) {
      const serverHash = this.lesson.server_bundle_hash;
      if (serverHash !== '' && serverHash !== lessonBundleHash) {
        const storageKey = 'lastLessonBundleReload';
        const existingValue = localStorage.getItem(storageKey);

        if (existingValue !== lessonBundleHash) {
          localStorage.setItem(storageKey, lessonBundleHash);
          window.location.reload();
        }
      }
      await this.fetchLesson();
    } else {
      if (!this.lesson.compiled_js || !this.lesson.interactives_manifest) {
        this.loadingError = 'missingJs';
      } else {
        await this.loadLessonJSON({
          interactivesManifest: this.lesson.interactives_manifest,
          compiledJs: this.lesson.compiled_js,
          contentHashes: this.lesson.content_hashes,
        });
      }
    }
    loadSounds();

    if (this.$route.query.iframe) {
      const djdtTab = document.getElementById('djDebugToolbarHandle');
      if (djdtTab) {
        djdtTab.style.transform = 'translateY(999px)';
      }
    }

    // Don't record staff users testing things
    if (!this.user.is_staff) {
      initHotjar(this.$store);
    }
  }

  private beforeDestroy() {
    if (this.reloadTimeout) {
      clearTimeout(this.reloadTimeout);
    }
    this?.elmApp?.ports?.fromJs.send({ name: 'destroy' });
    if (this.$refs.lesson instanceof Node) {
      this.$refs.lesson.remove();
    } else if (this.$refs.lesson instanceof Array) {
      this.$refs.lesson.forEach(node => {
        if (node instanceof Node) {
          node.remove();
        }
      });
    }
    if (this.elmScript) {
      this.elmScript.remove();
      (window as any).Elm = undefined;
    }
    this.destroyBanner();
    globalStyles.unuse();
  }

  private async closeLesson() {
    if (nativeBridgeIsPresent()) {
      sendMessageAcrossNativeBridge('closeLesson');
      return;
    }

    if (this.isLoggedOut) {
      if (this.externalReferrer) {
        // try a full page reload since $router.push is not triggering the backend data fetch
        window.location.href = this.$router.resolve({ name: 'signup_nux' }).href;
      } else {
        await this.$router.go(-1);
      }
      return;
    }

    await this.$router.push({ path: this.icpUrl, query: { from_llp: this.$route.query.from_llp } });
  }

  private async goToAllCourses() {
    if (nativeBridgeIsPresent()) {
      sendMessageAcrossNativeBridge('browseAllCourses');
      return;
    }

    await this.$router.push({ name: 'explorations_menu' });
  }
}
